import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { FileSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { KnowledgeManagerLoader } from '@/UI/KnowledgeBase/KnowledgeManager/KnowledgeManager.loader';
import { hasSonyAdminAccess } from '@/UI/KnowledgeBase/SonyDocuments/sonyAdminAccess';

const getProgressColor = (percent: number) => {
  if (percent >= 100) return 'bg-destructive';
  if (percent >= 90) return 'bg-warning';
  return 'bg-foreground';
};

const getTextColor = (percent: number) => {
  if (percent >= 100) return 'text-destructive';
  if (percent >= 90) return 'text-warning';
  return 'text-muted-foreground';
};

const HeaderUsage = () => {
  const { t } = useTranslation(['knowledge_base']);
  const { getConfig } = useSubscription();
  const articlesLimit = getConfig('articles_limit') as number | null | undefined;
  const articlesUsed = (getConfig('articles_used') as number | undefined) ?? 0;

  // Custom (Enterprise) tier has no limit; loading state also lands here.
  if (articlesLimit === null || articlesLimit === undefined) return null;

  const percentUsed = Math.min(100, (articlesUsed / articlesLimit) * 100);

  return (
    <div
      className="flex items-center gap-2"
      data-testid="kb-header-usage"
      role="group"
      aria-label={t('usage_header.label', {
        used: articlesUsed,
        limit: articlesLimit,
      })}
    >
      <span className="text-xs font-medium text-muted-foreground">{t('usage_header.title')}</span>
      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted-foreground/15">
        <div
          className={classNames(
            'h-full rounded-full transition-all duration-500 ease-out',
            getProgressColor(percentUsed),
          )}
          style={{ width: `${percentUsed}%` }}
        />
      </div>
      <span className={classNames('text-xs font-medium tabular-nums', getTextColor(percentUsed))}>
        {t('usage_header.count', {
          used: articlesUsed.toLocaleString(),
          limit: articlesLimit.toLocaleString(),
        })}
      </span>
    </div>
  );
};

export const KnowledgeBasePage = () => {
  const { t } = useTranslation(['knowledge_base', 'sony_admin']);
  const queryClient = useQueryClient();
  const account = queryClient.getQueryData<AccountDto>(['account']);

  return (
    <div className="flex flex-1 flex-col min-w-0 max-w-full">
      <div className="mb-6 flex flex-col items-start gap-2 md:flex-row md:items-baseline md:gap-6">
        <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
        <HeaderUsage />
        <div className="flex items-center gap-2 md:ml-auto">
          {hasSonyAdminAccess(account) && (
            <Button variant="secondary" size="sm" asChild data-testid="sony-admin-entry-button">
              <Link to="sony">
                <FileSearch className="size-4" />
                {t('sony_admin:entry_button')}
              </Link>
            </Button>
          )}
          <ReadDocumentationButton href="https://docs.quickchat.ai/ai-agent/knowledge-base" />
        </div>
      </div>
      <KnowledgeManagerLoader />
      {/* Outlet renders ArticleSheet modal when visiting /articles/:id routes */}
      <Outlet />
    </div>
  );
};
